import { useCallback, useEffect, useRef, useState } from 'react';

export interface VersionInfo {
  version: string;
  buildTime: string;
  gitCommit?: string;
}

export interface VersionCheckState {
  currentVersion: string;
  latestVersion: string | null;
  isUpdateAvailable: boolean;
  isChecking: boolean;
  lastChecked: Date | null;
  error: string | null;
}

interface UseVersionCheckOptions {
  checkInterval?: number; // in milliseconds, default 5 minutes
  enabled?: boolean;
  onUpdateAvailable?: (latestVersion: string) => void;
}

const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_ENDPOINT = '/api/version';

// Get check interval from environment variable or use default
const getCheckInterval = (): number => {
  const envInterval = process.env.REACT_APP_VERSION_CHECK_INTERVAL;
  if (envInterval) {
    const parsed = parseInt(envInterval, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_CHECK_INTERVAL;
};

// Get force reload setting from environment variable
const getForceReloadEnabled = (): boolean => {
  return process.env.REACT_APP_VERSION_FORCE_RELOAD === 'true';
};

// Get current version from build-time injection
const getCurrentVersion = (): string => {
  // Use build-time injected commit SHA as the version identifier
  return process.env.REACT_APP_BUILD_COMMIT_SHA || 'unknown';
};

export const useVersionCheck = (options: UseVersionCheckOptions = {}) => {
  const {
    checkInterval = getCheckInterval(),
    enabled = true,
    onUpdateAvailable,
  } = options;

  const [state, setState] = useState<VersionCheckState>({
    currentVersion: getCurrentVersion(),
    latestVersion: null,
    isUpdateAvailable: false,
    isChecking: false,
    lastChecked: null,
    error: null,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const onUpdateAvailableRef = useRef(onUpdateAvailable);

  // Update callback ref when it changes
  useEffect(() => {
    onUpdateAvailableRef.current = onUpdateAvailable;
  }, [onUpdateAvailable]);

  const checkVersion = useCallback(async () => {
    if (!enabled) return;

    setState((prev: VersionCheckState) => ({
      ...prev,
      isChecking: true,
      error: null,
    }));

    try {
      // Add cache-busting parameter to ensure we get the latest version
      const cacheBuster = Date.now();
      const response = await fetch(`${VERSION_ENDPOINT}?t=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch version info: ${response.status}`);
      }

      const versionInfo: VersionInfo = await response.json();
      const latestCommitSha = versionInfo.gitCommit || 'unknown';
      const currentCommitSha = getCurrentVersion();

      // Compare commit SHAs to detect updates
      const isUpdateAvailable =
        latestCommitSha !== currentCommitSha && latestCommitSha !== 'unknown';

      setState((prev: VersionCheckState) => ({
        ...prev,
        latestVersion: versionInfo.version,
        isUpdateAvailable,
        isChecking: false,
        lastChecked: new Date(),
        error: null,
      }));

      // Call the callback if an update is available
      if (isUpdateAvailable && onUpdateAvailableRef.current) {
        onUpdateAvailableRef.current(versionInfo.version);
      }
    } catch (error) {
      console.warn('Version check failed:', error);
      setState((prev: VersionCheckState) => ({
        ...prev,
        isChecking: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, [enabled]);

  // Manual refresh function
  const refreshPage = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('_refresh', Date.now().toString());
    window.location.href = url.toString();
  }, []);

  // Set up periodic checking
  useEffect(() => {
    if (!enabled) return;

    // Check immediately on mount
    checkVersion();

    // Set up interval for periodic checks
    if (checkInterval > 0) {
      intervalRef.current = setInterval(checkVersion, checkInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkInterval, checkVersion]);

  // Check when page becomes visible (user returns to tab)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if it's been more than the check interval since last check
        const now = new Date();
        const lastCheck = state.lastChecked;

        if (!lastCheck || now.getTime() - lastCheck.getTime() > checkInterval) {
          checkVersion();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkInterval, checkVersion, state.lastChecked]);

  return {
    ...state,
    checkVersion,
    refreshPage,
    forceReloadEnabled: getForceReloadEnabled(),
  };
};
